import { useRef, useState } from "react";
import { Play, Pause, Wand2 } from "lucide-react";
import { card, label } from "@/lib/styles";

function formatTime(t) {
  if (!Number.isFinite(t) || t < 0) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function Narrator({ src, label: narratorLabel = "Altaris narrates this chapter" }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [failed, setFailed] = useState(false);

  if (failed) return null;

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    // drive `playing` off the element's own play/pause events, not optimistically —
    // play() can reject (autoplay policy, decode error) and desync the button.
    if (audio.paused) audio.play().catch(() => {});
    else audio.pause();
  };

  const seek = (e) => {
    const audio = audioRef.current;
    if (!audio) return;
    const t = Number(e.target.value);
    audio.currentTime = t;
    setCurrent(t);
  };

  return (
    <div className={`${card} px-4 py-3 mb-4 flex items-center gap-3`} data-testid="narrator">
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        className="hidden"
        onTimeUpdate={(e) => setCurrent(e.target.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onError={() => setFailed(true)}
      />
      <button
        data-testid="narrator-toggle"
        onClick={toggle}
        aria-label={playing ? "Pause narration" : "Play narration"}
        className="shrink-0 rounded-full border border-gold/60 text-gold w-8 h-8 flex items-center justify-center hover:bg-gold/10 transition-colors"
      >
        {playing ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
      </button>
      <div className="min-w-0 flex-1">
        <span className={`${label} flex items-center gap-1.5 !text-[10px] !tracking-[0.16em] truncate`}>
          <Wand2 size={11} className="shrink-0" />
          <span className="truncate">{narratorLabel}</span>
        </span>
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={current}
          onChange={seek}
          className="w-full h-1 mt-1 accent-emerald-400 cursor-pointer"
        />
      </div>
      <span className="shrink-0 text-[11px] font-mono text-zinc-500 tabular-nums">
        {formatTime(current)} / {formatTime(duration)}
      </span>
    </div>
  );
}
